import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Calendar, AlertCircle } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import AppointmentCard from './AppointmentCard';
import { AppointmentDetails } from './AppointmentDetails';
import { BookAppointment } from './BookAppointment';

export const Appointments = () => {
  const navigate = useNavigate();
  const { get, post, put } = useApi();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await get('/appointments');

      if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
        setAppointments(response.data.data);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const response = await put(`/appointments/${appointmentId}/status`, {
        status: newStatus
      });

      if (response.data?.status === 'success') {
        if (newStatus === 'in-progress') {

          navigate(`/counselor/sessions/${appointmentId}`);

        } else {
          fetchAppointments();
        }
      }
    } catch (error) {
      console.error('Failed to update appointment status:', error);
    }
  };
  const filteredAppointments = appointments.filter(appointment => {
    if (!appointment) return false;

    const studentId = appointment.client?.studentInfo?.studentId;
    const staffId = appointment.client?.staffInfo?.staffId;

    const matchesFilter = filter === 'all' || appointment.status === filter;

    if (!searchQuery.trim()) return matchesFilter;

    const searchLower = searchQuery.toLowerCase().trim();

    // Get search fields
    const clientName = appointment.client?.username?.toLowerCase() || '';
    const counselorName = appointment.counselor?.username?.toLowerCase() || '';
    const studentIdLower = studentId?.toString().toLowerCase() || '';
    const staffIdLower = staffId?.toString().toLowerCase() || '';

    const matchesSearch =
      clientName.includes(searchLower) ||
      counselorName.includes(searchLower) ||
      studentIdLower.includes(searchLower) ||
      staffIdLower.includes(searchLower);;

    return matchesFilter && matchesSearch;
  });


  const canBookAppointments = ['student', 'receptionist', 'staff'].includes(user?.role);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uzima-maroon-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      );
    }

    if (!appointments.length) {
      return (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">No appointments found</h3>
          <p className="mt-1 text-gray-500">
            {canBookAppointments ? 'Click "Book Appointment" to get started' : 'No appointments available at this time'}
          </p>
        </div>
      );
    }

    if (!filteredAppointments.length) {
      return (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">No matching appointments</h3>
          <p className="mt-1 text-gray-500">Try adjusting your search or filter criteria</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAppointments.map(appointment => (
          <AppointmentCard
            key={appointment._id}
            appointment={appointment}
            onViewDetails={() => setSelectedAppointmentId(appointment._id)}
            onStatusChange={(status) => handleStatusChange(appointment._id, status)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Appointments</h1>
        {canBookAppointments && (
          <button
            onClick={() => setShowBooking(true)}
            className="flex items-center px-4 py-2 text-sm ml-4 sm:text-base sm:px-4 sm:py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600 transition-colors duration-200"
          >
            <Plus className="h-4 w-4 mr-2 sm:h-5 sm:w-5" />
            Book Appointment
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search appointments..."
                className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              className="border rounded-md px-3 py-2"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no-show">No Show</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {renderContent()}

      {/* Modals */}
      {showBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <BookAppointment
              onClose={() => {
                setShowBooking(false);
                fetchAppointments();
              }}
            />
          </div>
        </div>
      )}

      {selectedAppointmentId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <AppointmentDetails
              appointmentId={selectedAppointmentId}
              onClose={() => {
                setSelectedAppointmentId(null);
                fetchAppointments();
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Appointments;